import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const TermsOfServicePage = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="terms-page">
    <Helmet>
      <title>Terms of Service | Common Impact E-Learning</title>
      <meta name="description" content="Terms of Service for Common Impact E-Learning platform. Rules for using our care sector training platform." />
    </Helmet>
    <Header />
    <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
      <h1 className="text-3xl sm:text-4xl font-bold text-[#2a3e5d] mb-2">Terms of Service</h1>
      <p className="text-sm text-slate-500 mb-8">Last updated: 1 June 2026</p>

      <div className="prose prose-slate max-w-none prose-headings:text-[#2a3e5d] prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h3:text-lg prose-p:text-sm prose-p:leading-relaxed prose-li:text-sm">
        <h2>1. Introduction</h2>
        <p>These Terms of Service ("Terms") govern your use of the Common Impact E-Learning platform ("the Platform") operated by Common Impact E-Learning ("we", "us", "our"), located at 71-75 Shelton Street, London, WC2H 9JQ, England. By creating an account or using the Platform, you agree to these Terms.</p>

        <h2>2. User Accounts</h2>
        <h3>2.1 Account Creation</h3>
        <p>You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your login credentials and for all activities under your account.</p>
        <h3>2.2 Account Types</h3>
        <ul>
          <li><strong>Individual Learner:</strong> Personal accounts for individuals accessing courses</li>
          <li><strong>Organisation Admin:</strong> Accounts that manage team members, assign courses, and view compliance reports</li>
          <li><strong>Content Manager:</strong> Accounts with course creation and management privileges</li>
          <li><strong>Super Admin:</strong> Full platform administration access</li>
        </ul>
        <h3>2.3 Account Security</h3>
        <p>You must not share your account credentials. If you suspect unauthorised access, contact us immediately at <a href="mailto:admin@commonimpactelearning.co.uk" className="text-[#20acf6]">admin@commonimpactelearning.co.uk</a>.</p>

        <h2>3. Course Access and Completion</h2>
        <h3>3.1 Accessing Courses</h3>
        <p>Courses may be accessed through individual purchase, bundle purchase, or organisation subscription, depending on the course and your account type.</p>
        <h3>3.2 Course Completion</h3>
        <p>Courses are marked as complete only when all required elements have been finished, including:</p>
        <ul>
          <li>Watching videos to the required completion threshold (typically 90%)</li>
          <li>Reading all text content (scroll-to-bottom verification)</li>
          <li>Completing quizzes with a passing score (typically 70%)</li>
          <li>Completing all interactive exercises (matching, fill-the-gaps, case studies)</li>
        </ul>
        <h3>3.3 Certificates</h3>
        <p>Certificates are issued only upon 100% course completion. Certificates include a unique verification number and QR code. Employers and regulators can verify certificates via our public verification page. Certificates may carry an expiry date where applicable.</p>

        <h2>4. Payments and Subscriptions</h2>
        <h3>4.1 Individual Purchases</h3>
        <p>Individual courses and bundles may be purchased as one-time payments. Prices are displayed in GBP and are inclusive of VAT where applicable.</p>
        <h3>4.2 Organisation Subscriptions</h3>
        <p>Organisation subscriptions provide access to a library of courses for a recurring monthly or annual fee. Subscription tiers (Essentials, Professional, Business, Enterprise) offer different user limits and feature sets.</p>
        <h3>4.3 Billing</h3>
        <p>Payments are processed securely by Stripe. We do not store card details on our servers. Subscriptions auto-renew unless cancelled before the renewal date.</p>
        <h3>4.4 Refunds</h3>
        <p>Refund requests must be submitted within 14 days of purchase. Courses that have been completed (100%) are not eligible for refund. Contact <a href="mailto:admin@commonimpactelearning.co.uk" className="text-[#20acf6]">admin@commonimpactelearning.co.uk</a> for refund requests.</p>

        <h2>5. Organisation Accounts</h2>
        <p>Organisation admins are responsible for:</p>
        <ul>
          <li>Managing team member access and permissions</li>
          <li>Ensuring team members comply with these Terms</li>
          <li>Maintaining accurate organisation information</li>
          <li>Managing subscription payments and renewals</li>
        </ul>
        <p>Organisation admins may view team members' learning progress, completion records, and compliance reports. Individual quiz answers and reflection journals are not visible to organisation admins.</p>

        <h2>6. Intellectual Property</h2>
        <h3>6.1 Platform Content</h3>
        <p>All course content, including text, videos, images, quizzes, and interactive elements, is owned by Common Impact E-Learning or its licensors. You may not reproduce, distribute, or create derivative works from our content without written permission.</p>
        <h3>6.2 User-Generated Content</h3>
        <p>Content you create on the platform (e.g., reflection journals, case study responses, forum posts) remains your intellectual property. By submitting content, you grant us a non-exclusive licence to store and display it within the Platform for the purpose of providing the service.</p>

        <h2>7. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Platform for any unlawful purpose</li>
          <li>Share or distribute course content outside the Platform</li>
          <li>Attempt to circumvent progress tracking or assessment systems</li>
          <li>Create multiple accounts to bypass platform restrictions</li>
          <li>Upload malicious content, viruses, or harmful code</li>
          <li>Harass, abuse, or threaten other users</li>
          <li>Use automated tools (bots, scrapers) to access the Platform</li>
        </ul>

        <h2>8. Termination and Suspension</h2>
        <h3>8.1 By You</h3>
        <p>You may close your account at any time by contacting us or using the account deletion feature. Completed certificates and learning records will be retained as described in our Privacy Policy.</p>
        <h3>8.2 By Us</h3>
        <p>We may suspend or terminate your account if you:</p>
        <ul>
          <li>Breach these Terms of Service</li>
          <li>Engage in fraudulent or abusive behaviour</li>
          <li>Fail to pay subscription fees (for organisation accounts)</li>
        </ul>
        <p>We will provide reasonable notice before termination, except in cases of serious breach.</p>

        <h2>9. Liability and Disclaimers</h2>
        <h3>9.1 Platform Availability</h3>
        <p>We aim to provide continuous access to the Platform but do not guarantee uninterrupted service. We are not liable for any loss resulting from service interruptions, maintenance, or technical issues.</p>
        <h3>9.2 Course Content</h3>
        <p>Our courses are designed to support professional development and regulatory compliance. However, they do not constitute legal, medical, or professional advice. You should always follow your organisation's specific policies and procedures.</p>
        <h3>9.3 Limitation of Liability</h3>
        <p>To the fullest extent permitted by law, our total liability to you shall not exceed the amount you have paid to us in the 12 months preceding the claim. We are not liable for any indirect, consequential, or incidental damages.</p>

        <h2>10. Governing Law</h2>
        <p>These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the English courts.</p>

        <h2>11. Changes to These Terms</h2>
        <p>We may update these Terms from time to time. The "Last updated" date at the top will reflect the most recent version. Material changes will be notified via email or platform notification. Continued use of the Platform after changes constitutes acceptance of the updated Terms.</p>

        <h2>12. Contact Us</h2>
        <p>If you have questions about these Terms, please contact us:</p>
        <p><strong>Common Impact E-Learning</strong><br />71-75 Shelton Street, London, WC2H 9JQ, England<br />Email: <a href="mailto:admin@commonimpactelearning.co.uk" className="text-[#20acf6]">admin@commonimpactelearning.co.uk</a></p>
      </div>
    </main>
    <Footer />
  </div>
);

export default TermsOfServicePage;
